import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './api';
import {
  DEFAULT_FEED_STYLE,
  type CatalogProduct,
  type FeedStyle,
} from '@nopain-newsletter/core/blocks';
import type { BrandKit, CatalogResponse } from '../types/api';

export const CATALOG_KEY = ['catalog'] as const;

/**
 * The tenant's product catalogue (K4 product feed block): one query shared by
 * the block editor and the live preview. A failed load gives an empty list, so
 * the preview shows nothing for a feed and the server still fills it correctly
 * when the mail is prepared.
 */
export function useCatalog() {
  const query = useQuery({
    queryKey: CATALOG_KEY,
    queryFn: () => apiFetch<CatalogResponse>('/catalog'),
    staleTime: 30_000,
  });
  const products = useMemo(() => query.data?.products ?? [], [query.data]);
  return { ...query, products, refreshedAt: query.data?.refreshedAt ?? null };
}

/** What the live preview needs to fill a product feed: the catalogue and the brand colours the sent mail will use. */
export function useFeedPreview(): { products: CatalogProduct[]; style: FeedStyle } {
  const { products } = useCatalog();
  const { data: kit } = useQuery({
    queryKey: ['brand-kit'],
    queryFn: () => apiFetch<BrandKit>('/brand-kit'),
  });
  const primaryColor = kit?.primaryColor || DEFAULT_FEED_STYLE.primaryColor;
  const textColor = kit?.textColor || DEFAULT_FEED_STYLE.textColor;
  return useMemo(
    () => ({ products, style: { primaryColor, textColor } }),
    [products, primaryColor, textColor],
  );
}

/** "Refresh from shop": asks the shop for its products and replaces the catalogue. */
export function useRefreshCatalog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<CatalogResponse>('/catalog/refresh', { method: 'POST' }),
    onSuccess: (data) => queryClient.setQueryData(CATALOG_KEY, data),
  });
}
